import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock3, Copy, Share2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NieuwsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);

    axios
      .get(`${API}/news/${id}`)
      .then((response) => {
        setArticle(response.data);
        return axios.get(`${API}/news`);
      })
      .then((response) => {
        const items = Array.isArray(response.data) ? response.data : [];
        setRelated(items.filter((item) => item.id !== id).slice(0, 3));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString('nl-BE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';

  const estimateReadTime = (html) => {
    const plain = (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = plain ? plain.split(' ').length : 0;
    const minutes = Math.max(1, Math.round(words / 220));
    return `${minutes} min leestijd`;
  };

  const shareArticle = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: article?.titel || 'Nieuwsartikel',
          text: article?.samenvatting || '',
          url,
        });
        return;
      }

      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  if (loading) {
    return (
      <div className='pt-20 min-h-screen flex items-center justify-center bg-white' data-testid='nieuws-detail-loading'>
        <p className='text-[#6b7184]'>Artikel laden...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className='pt-20 min-h-screen flex items-center justify-center admin-page' data-testid='nieuws-detail-error'>
        <div className='text-center px-6'>
          <h1 className='text-2xl font-bold text-[#16224b] mb-4'>Artikel niet gevonden</h1>
          <Link to='/nieuws' className='text-[#3d56a6] hover:underline'>
            Terug naar nieuws
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid='nieuws-detail-page'>
      <section
        className='relative overflow-hidden pt-32 pb-28'
        style={{
          backgroundImage: `linear-gradient(rgba(22, 34, 75, 0.84), rgba(22, 34, 75, 0.9)), url(${article.afbeelding || ''})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className='absolute inset-0 dot-pattern opacity-60' />
        <div className='relative z-10 max-w-[1000px] mx-auto px-6 lg:px-10'>
          <Link to='/nieuws' className='inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors'>
            <ArrowLeft className='w-4 h-4' /> Terug naar nieuws
          </Link>

          <h1 className='text-white font-bold tracking-tight mb-6' style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', letterSpacing: '-0.03em' }}>
            {article.titel}
          </h1>

          <div className='flex flex-wrap items-center gap-5 text-white/70 text-sm mb-6'>
            <span className='inline-flex items-center gap-2'>
              <Calendar className='w-4 h-4' />
              {formatDate(article.created_at || article.datum)}
            </span>
            <span className='inline-flex items-center gap-2'>
              <Clock3 className='w-4 h-4' />
              {estimateReadTime(article.inhoud)}
            </span>
            <button
              type='button'
              onClick={shareArticle}
              className='inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/15 text-white transition-colors'
            >
              {copied ? <Copy className='w-4 h-4' /> : <Share2 className='w-4 h-4' />}
              {copied ? 'Gekopieerd' : 'Delen'}
            </button>
          </div>

          {article.samenvatting && (
            <p className='text-white/75 text-lg leading-8 max-w-3xl'>{article.samenvatting}</p>
          )}
        </div>
      </section>

      <section className='py-20 bg-white'>
        <div className='max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_300px] gap-10 items-start'>
          <div className='article-content' dangerouslySetInnerHTML={{ __html: article.inhoud || '' }} />

          <aside className='lg:sticky lg:top-28 admin-surface p-6'>
            <h3 className='text-[#16224b] font-bold mb-4'>Artikel informatie</h3>
            <div className='space-y-3 text-sm text-[#6b7184]'>
              <p>
                <span className='text-[#16224b] font-semibold'>Categorie:</span> {article.categorie || 'Nieuws'}
              </p>
              <p>
                <span className='text-[#16224b] font-semibold'>Gepubliceerd:</span> {formatDate(article.created_at || article.datum)}
              </p>
              <p>
                <span className='text-[#16224b] font-semibold'>Leestijd:</span> {estimateReadTime(article.inhoud)}
              </p>
            </div>
            <button
              type='button'
              onClick={shareArticle}
              className='btn-outline-dark w-full mt-6 justify-center'
            >
              Deel dit artikel <Share2 className='w-4 h-4' />
            </button>
            <Link to='/nieuws' className='btn-gradient w-full mt-3 justify-center'>
              Alle artikels <ArrowRight className='w-4 h-4' />
            </Link>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className='py-20 bg-[#f8f9fd]'>
          <div className='max-w-[1400px] mx-auto px-6 lg:px-10'>
            <div className='flex items-end justify-between mb-10 gap-6'>
              <div>
                <h2 className='text-[#16224b] font-bold' style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
                  Gerelateerde artikels
                </h2>
              </div>
              <Link to='/nieuws' className='btn-outline-dark shrink-0'>
                Bekijk alles <ArrowRight className='w-4 h-4' />
              </Link>
            </div>

            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-7'>
              {related.map((item, idx) => (
                <Link
                  key={item.id || idx}
                  to={`/nieuws/${item.id}`}
                  className='group bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300'
                >
                  {item.afbeelding && (
                    <div className='overflow-hidden' style={{ height: 190 }}>
                      <img src={item.afbeelding} alt={item.titel} className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500' />
                    </div>
                  )}
                  <div className='p-6'>
                    <p className='text-xs text-[#6b7184] mb-3'>
                      {formatDate(item.created_at || item.datum)}
                    </p>
                    <h3 className='text-[#16224b] font-bold text-lg mb-3 line-clamp-2 group-hover:text-[#3d56a6] transition-colors'>
                      {item.titel}
                    </h3>
                    <p className='text-[#6b7184] text-sm leading-7 line-clamp-3'>
                      {item.samenvatting || item.inhoud?.replace(/<[^>]+>/g, ' ').substring(0, 120)}
                    </p>
                    <span className='inline-flex items-center gap-1 text-[#3d56a6] text-sm font-semibold mt-4'>
                      Lees meer <ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}